import EventEmitter from '../Utils/EventEmitter';
import { Sizes } from './Sizes';
import '../../js/vendors/lenis/lenis.min.js';
import '../../js/vendors/lenis/lenis.css';

/**
 * SmoothScroll class
 * Handles scroll behavior using Lenis smooth scroll
 */
export class SmoothScroll extends EventEmitter {
    // Static instance property
    static instance = null;

    /**
     * Get the singleton instance of the class
     * @returns {SmoothScroll} The singleton instance
     */
    static getInstance() {
        if (!SmoothScroll.instance) {
            SmoothScroll.instance = new SmoothScroll();
        }
        return SmoothScroll.instance;
    }

    /**
     * Constructor
     */
    constructor() {
        // Ensure this is a singleton
        if (SmoothScroll.instance) {
            return SmoothScroll.instance;
        }

        super();

        // Scroll properties
        this.instance = null;
        this.scrollY = 0;
        this.limit = 0;
        this.progress = 0;
        this.isReady = false;
        this.sizes = Sizes.getInstance();
        this.isTouchDevice = this.sizes.isTouchDevice;
        this.previousScrollY = 0;

        // Settings
        this.settings = {
            duration: this.sizes.isDesktop ? 1.2 : 1,
            smoothTouch: false,
            syncTouch: false,
            direction: "vertical",
            gestureDirection: "vertical"
        };

        // Initialize
        this.init();

        // Set instance
        SmoothScroll.instance = this;
    }

    setRenderer(renderer) {
        this.renderer = renderer;
        this.canvas = this.renderer.instance.domElement;
    }

    /**
     * Initialize the scroll instance
     */
    init() {
        this.bindAll();
        this.initLenis();
    }

    bindAll() {
        this.onLenisScroll = this.onLenisScroll.bind(this);
        this.onResize = this.onResize.bind(this);
    }

    /**
     * Initialize Lenis Scroll
     */
    initLenis() {
        if (this.isTouchDevice) {
            document.body.classList.add('touch-device');
        }

        this.instance = new Lenis(this.settings);

        this.instance.on('scroll', this.onLenisScroll);

        this.update();
        this.isReady = true;
        this.trigger('ready');

        this.sizes.on('resize', this.onResize);
    }

    /**
     * Animation frame loop
     */
    raf(time) {
        if(this.isTouchDevice) {
            if(this.canvas) {
                this.canvas.style.transform = `translateY(${this.scrollY}px)`;
            }
        } else {
            this.instance?.raf(time);
        }
        
    }

    /**
     * Handle Lenis scroll event
     * @param {Object} obj - Lenis scroll event object
     */
    onLenisScroll(obj) {
        this.scrollY = obj.scroll;
        this.speed = obj.velocity;
        this.limit = this.sizes.scrollableHeight;
        this.progress = this.limit > 0 ? this.scrollY / this.limit : 0;

        // Handle menu visibility based on scroll direction
        if (this.scrollY >= 100) {
            const delta = this.scrollY - this.previousScrollY;
            const direction = delta > 0 ? 'down' : delta < 0 ? 'up' : 'none';
            if (direction === 'down') {
                document.body.classList.add('hide-menu');
            } else if (direction === 'up') {
                document.body.classList.remove('hide-menu');
            }
        } else {
            document.body.classList.remove('hide-menu');
        }
        this.previousScrollY = this.scrollY;

        // Use a shared object to reduce garbage collection
        if (!this._scrollData) {
            this._scrollData = { y: this.scrollY, limit: this.limit, progress: this.progress, speed: this.speed };
        } else {
            this._scrollData.y = this.scrollY;
            this._scrollData.limit = this.limit;
            this._scrollData.progress = this.progress;
            this._scrollData.speed = this.speed;
        }

        // Trigger scroll event
        this.trigger('scroll', [this._scrollData]);
    }

    /**
     * Handle resize event
     */
    onResize() {
        // Update the scroll instance after a short delay
        setTimeout(() => {
            this.update();
        }, 100);
    }

    /**
     * Scroll to specific position or element
     * @param {Number|HTMLElement|String} target - Target position, element, or selector
     * @param {Object} options - Scroll options
     */
    scrollTo(target, options = {}) {
        if (!this.instance) return;

        // Default options
        const defaultOptions = {
            offset: 0,
            immediate: false,
            duration: 1.0,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            callback: null
        };

        // Merge options
        const scrollOptions = { ...defaultOptions, ...options };

        let targetPosition = 0;

        // Determine target position
        if (typeof target === 'number') {
            // Direct position
            targetPosition = target;
        } else {
            // Element or selector
            const targetElement = typeof target === 'string'
                ? document.querySelector(target)
                : target;

            if (targetElement) {
                const rect = targetElement.getBoundingClientRect();
                targetPosition = rect.top + window.scrollY + scrollOptions.offset;
            }
        }

        // Use Lenis scrollTo
        this.instance.scrollTo(targetPosition, {
            duration: scrollOptions.immediate ? 0 : scrollOptions.duration * 1000,
            easing: scrollOptions.easing,
            callback: scrollOptions.callback
        });
    }

    /**
     * Update scroll (e.g. after content changes)
     */
    update() {
        if (this.instance) {
            this.instance.resize();
            this.trigger('updated');
        }
    }

    /**
     * Get current scroll position
     * @returns {Number} Current scroll position
     */
    getScrollY() {
        return this.scrollY;
    }

    /**
     * Get maximum scroll value
     * @returns {Number} Maximum scroll value
     */
    getLimit() {
        return this.limit;
    }

    /**
     * Get scroll progress (0-1)
     * @returns {Number} Scroll progress
     */
    getProgress() {
        return this.progress;
    }

    /**
     * Check if smooth scroll is initialized and ready
     * @returns {Boolean} True if ready
     */
    isInitialized() {
        return this.isReady;
    }

    /**
     * Destroy the scroll instance
     */
    destroy() {
        if (this.instance) {
            this.instance.destroy();
        }

        window.removeEventListener('resize', this.onResize.bind(this));
        this.trigger('destroyed');
    }
}
